<script setup lang="ts">
import { computed } from 'vue';
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import StatusCard from '@/V2/components/status/StatusCard.vue';
import AiLabel from '@/V2/components/AiLabel.vue';
import UserFeedback from '@/V2/components/user-feedback/UserFeedback.vue';
import { Statuses } from '@/V2/components/status/utils';
import { RISK_LEVEL, riskLevelToReadableLabel } from '@/constants/riskLevels';
import {
  CustomerFraudReasoningResult,
  CustomerFraudReasoningStatusForUi,
} from '@/utils/customerFraudReasoning';
import { formatDuration } from '@/utils/date';
import { USER_FEEDBACK_RESOURCE_TYPE } from '@/constants/userFeedback';

interface Props {
  status: CustomerFraudReasoningStatusForUi;
  result?: CustomerFraudReasoningResult | null;
}

const props = defineProps<Props>();

const statusCardState = computed(() => {
  if (
    props.status in
    [
      CustomerFraudReasoningStatusForUi.PROCESSING_DOCUMENTS,
      CustomerFraudReasoningStatusForUi.ANALYZING_CUSTOMER,
      CustomerFraudReasoningStatusForUi.REASONING,
    ]
  ) {
    return Statuses.LOADING;
  }
  if (props.status === CustomerFraudReasoningStatusForUi.FAILED) {
    return Statuses.FAILED;
  }
  if (props.status === CustomerFraudReasoningStatusForUi.NO_DATA) {
    return Statuses.NOT_APPLICABLE;
  }
  if (
    props.status === CustomerFraudReasoningStatusForUi.FINISHED &&
    props.result?.riskLevel
  ) {
    const { riskLevel } = props.result;
    if (riskLevel === RISK_LEVEL.HIGH) {
      return Statuses.ERROR;
    } else if (riskLevel === RISK_LEVEL.MEDIUM) {
      return Statuses.WARNING;
    } else if (riskLevel === RISK_LEVEL.LOW) {
      return Statuses.SUCCESS;
    } else if (riskLevel === RISK_LEVEL.NOT_APPLICABLE) {
      return Statuses.NOT_APPLICABLE;
    }
  }

  return Statuses.LOADING;
});

const statusCardLabel = computed(() => {
  if (props.status === CustomerFraudReasoningStatusForUi.PROCESSING_DOCUMENTS) {
    return 'Processing documents';
  }
  if (props.status === CustomerFraudReasoningStatusForUi.ANALYZING_CUSTOMER) {
    return 'Analyzing customer';
  }
  if (props.status === CustomerFraudReasoningStatusForUi.REASONING) {
    return 'Reasoning';
  }
  if (props.status === CustomerFraudReasoningStatusForUi.FAILED) {
    return 'Failed';
  }
  if (props.status === CustomerFraudReasoningStatusForUi.NO_DATA) {
    return 'No data';
  }
  if (
    props.status === CustomerFraudReasoningStatusForUi.FINISHED &&
    props.result?.riskLevel
  ) {
    return riskLevelToReadableLabel(props.result.riskLevel);
  }

  return '';
});

const thinkingDuration = computed(() => {
  if (props.result?.startedAt && props.result?.finishedAt) {
    const duration = formatDuration(
      new Date(props.result.startedAt),
      new Date(props.result.finishedAt),
    );
    return duration ? `reasoned for ${duration}` : undefined;
  }
  return undefined;
});

const shouldShowThinkingDuration = computed(() => {
  return (
    props.status === CustomerFraudReasoningStatusForUi.FINISHED &&
    thinkingDuration.value
  );
});
</script>

<template>
  <div class="customer-fraud-reasoning-analysis">
    <ExpandableContainer title="Analysis">
      <template #items>
        <div class="analysis-container i-p-8 d-flex flex-column i-gap-4">
          <StatusCard
            :state="statusCardState"
            :label="statusCardLabel"
            :hover-state="false"
          />
          <div
            v-if="
              status === CustomerFraudReasoningStatusForUi.FINISHED &&
              result?.id
            "
            class="d-flex flex-row justify-content-between align-items-center i-px-4 i-pt-3"
          >
            <div
              v-if="shouldShowThinkingDuration"
              class="d-flex flex-row align-items-center i-gap-2"
            >
              <AiLabel text="AI Agent" />
              <div class="thinking-duration i-text-body-3 text-neutral">
                {{ thinkingDuration }}
              </div>
            </div>
            <div class="d-flex flex-row align-items-center i-gap-2">
              <span class="i-text-body-3 text-neutral"
                >Was this result accurate?</span
              >
              <UserFeedback
                :resource-type="
                  USER_FEEDBACK_RESOURCE_TYPE.CUSTOMER_FRAUD_REASONING_AGENT_RUN
                "
                :resource-id="result.id"
              />
            </div>
          </div>
        </div>
      </template>
    </ExpandableContainer>
  </div>
</template>

<style lang="scss" scoped>
.customer-fraud-reasoning-analysis {
  .analysis-container {
    background: var(--base);

    .status-card {
      width: 100%;
    }
  }
}
</style>
